import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import { getAssessmentByID, updateAssessment } from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";

const EditRespirationModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    value: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  const { data: respiration, isLoading, refetch } = useQuery(
    [QK.respiration, selectedID],
    () => getAssessmentByID(selectedID),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            value: data.value || "",
            notes: data.notes || "",
            date_time: data.date_time || "",
            handover: data.handover || false,
            manager: data.manager || false,
          });
        }
      },
    }
  );

  useEffect(() => {
    if (show && selectedID) {
      refetch();
    }
  }, [show, selectedID, refetch]);

  const updateMutation = useMutation(
    (newData) => updateAssessment(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Respiration entry updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.respirations]);
        queryClient.invalidateQueries([QK.respiration, selectedID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to update respiration entry", {
          variant: "error",
        });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleDialogClose = () => {
    setFormData({
      value: "",
      notes: "",
      date_time: "",
      handover: false,
      manager: false,
    });
    handleClose();
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Respiration Entry
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update respiration record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Respiration (per min) *"
                name="value"
                type="number"
                value={formData.value}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomDateField
                name="date_time"
                label="Date & Time *"
                value={formData.date_time}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextArea
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <CustomCheckbox
                  label="Share with Manager"
                  name="manager"
                  checked={formData.manager}
                  onClick={(checked) => handleCheckBox(checked, "manager")}
                />
                <CustomCheckbox
                  label="Handover"
                  name="handover"
                  checked={formData.handover}
                  onClick={(checked) => handleCheckBox(checked, "handover")}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isLoading}
            startIcon={updateMutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {updateMutation.isLoading ? "Saving..." : "Update Respiration"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditRespirationModal;